import {
  withLargeButtonAlignment,
  withRtlSupport,
} from '@skyscanner/backpack-web/bpk-component-icon';
import ArrowIconLg from '@skyscanner/backpack-web/bpk-component-icon/lg/long-arrow-right';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import DateRangeSelector from '../DateRangeSelector';
import DestinationSelector from '../DestinationSelector';
import GuestsRoomsChildrenSelect from '../GuestsRoomsChildrenSelect';

import Preference from './Preference';

import type { AutosuggestSuggestionsOrPopularDestinations } from '../../types';
import type { MBDProps } from '@shared/types/frontend/meaningful-behaviour-data';
import type { I18nService } from '@shared/types/i18n';
import type { Maybe } from '@shared/types/utils';
import type { Destination } from '@shared/ui/types';

import STYLES from './ExpandableLayout.module.scss';

import BpkLoadingButton from '@shared/ui/common/BpkLoadingButton';
import { withI18n } from '@shared/ui/contexts/i18n';
import { MeaningfulBehaviourDataWrapper } from '@shared/ui/contexts/meaningful-behaviour-data';

const cls = cssModules(STYLES);
const AlignedArrowIconLg = withLargeButtonAlignment(
  withRtlSupport(ArrowIconLg),
);

type Props = {
  i18n: I18nService;
  destination?: Destination;
  checkInDate: Maybe<Date>;
  checkOutDate: Maybe<Date>;
  destinationLabel: string;
  childrenAges: number[];
  adults: number;
  rooms: number;
  suggestions: AutosuggestSuggestionsOrPopularDestinations;
  onClearSuggestions: Function;
  onSuggestionSelected: Function;
  onSuggestionsFetchRequested: Function;
  onDatesChanged: Function;
  onGetPreference: Function;
  getGuestsRoomsChildren: Function;
  onSubmitClick: Function;
  submittingForm?: boolean;
  lightLabel?: boolean;
  validDestination: Maybe<boolean>;
  freeCancellation?: boolean;
  showPreference: boolean;
  arrangeInline?: boolean;
  masonry?: boolean;
  isFromSeoPage?: boolean;
  mbdProps: MBDProps;
};

const ExpandableLayout = ({
  adults,
  arrangeInline,
  checkInDate,
  checkOutDate,
  childrenAges,
  destination,
  destinationLabel,
  freeCancellation,
  getGuestsRoomsChildren,
  i18n: { translate },
  isFromSeoPage,
  lightLabel,
  masonry,
  mbdProps,
  onClearSuggestions,
  onDatesChanged,
  onGetPreference,
  onSubmitClick,
  onSuggestionSelected,
  onSuggestionsFetchRequested,
  rooms,
  showPreference,
  submittingForm,
  suggestions,
  validDestination,
}: Props) => (
  <>
    <div
      className={cls(
        'ExpandableLayout__inputs',
        arrangeInline && 'ExpandableLayout__inputsInline',
      )}
    >
      <DestinationSelector
        className={cls(
          'ExpandableLayout__location',
          arrangeInline && 'ExpandableLayout__locationInline',
          !isFromSeoPage ? 'ExpandableLayout__locationEnhance' : null,
        )}
        inputClassName={cls('ExpandableLayout__input')}
        destinationLabel={destinationLabel}
        destination={destination}
        lightLabel={lightLabel}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onClearSuggestions}
        suggestions={suggestions}
        arrangeInline={arrangeInline}
        valid={validDestination}
        masonry={masonry}
      />

      <div
        className={cls(
          'ExpandableLayout__inputsDatesGuest',
          arrangeInline && 'ExpandableLayout__inputsDatesGuestInline',
          !isFromSeoPage ? 'ExpandableLayout__inputsDatesGuestEnhance' : null,
        )}
        data-test-id="search-controls-datesguest"
      >
        <DateRangeSelector
          className={cls('ExpandableLayout__dateRange')}
          formFieldClassName={cls(
            'ExpandableLayout__formField',
            !isFromSeoPage ? 'ExpandableLayout__formFieldEnhance' : null,
          )}
          lightLabel={lightLabel}
          // @ts-expect-error TS2322: Type 'Maybe<Date>' is not assignable to type 'Date'.
          checkInDate={checkInDate}
          // @ts-expect-error TS2322: Type 'Maybe<Date>' is not assignable to type 'Date'.
          checkOutDate={checkOutDate}
          onDatesChanged={onDatesChanged}
          masonry={masonry}
          showLabel
        />
        <GuestsRoomsChildrenSelect
          className={cls('ExpandableLayout__guestsRoomsChildren')}
          label={translate('HotelStay_label_guests_rooms')}
          lightLabel={lightLabel}
          adults={adults}
          rooms={rooms}
          childrenAges={childrenAges}
          getGuestsRoomsChildren={getGuestsRoomsChildren}
          masonry={masonry}
        />
      </div>
    </div>
    <div
      className={cls(
        'ExpandableLayout__ctaWrapper',
        arrangeInline && 'ExpandableLayout__ctaWrapperInline',
        showPreference && 'ExpandableLayout__preferenceOnSearchBar',
        !isFromSeoPage ? 'ExpandableLayout__ctaWrapperEnhance' : null,
      )}
    >
      {showPreference && (
        <Preference
          className={cls('ExpandableLayout__preferenceContainer')}
          onGetPreference={onGetPreference}
          freeCancellation={freeCancellation}
        />
      )}
      <MeaningfulBehaviourDataWrapper {...mbdProps}>
        <BpkLoadingButton
          className={cls('ExpandableLayout__cta')}
          loading={submittingForm}
          submit
          featured
          large
          data-test-id="search-button"
          onClick={onSubmitClick}
          icon={<AlignedArrowIconLg />}
        >
          {freeCancellation && !showPreference
            ? translate('SearchControls_label_Submit_freeCancellation')
            : translate('SearchControls_label_Submit')}
        </BpkLoadingButton>
      </MeaningfulBehaviourDataWrapper>
    </div>
  </>
);

export default withI18n(ExpandableLayout);
